import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { useHistory } from 'react-router-dom'
import numeral from 'numeral'
import { useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'
import cn from 'classnames'

import styles from './styles.module.scss'

import { getInvoices, getPlaces, getCustomers, removeInvoice, updateInvoiceStatus, getInvoiceSettings } from '../../../api'
import { downloadInvoicePdf, previewInvoicePdfUrl, getGroupedItems, buildInvoiceContext, applyMarkUps } from '../../../helpers/invoice'
import { generateExcelExport } from '../../../helpers/invoice_export'
import { getCurrency } from '../../../helpers/currencies'

import usePermissions from '../../Core/usePermissions'
import CustomSelect from '../../Core/CustomSelect'
import DRP from '../../Core/DRP/DRP'
import Table from '../Table'
import PreviewInvoice from '../PreviewInvoice'
import { PlusIcon, ThreeDotsIcon, NoteIcon, ExcelIcon } from '../Icons'
import InvoiceStatus from '../InvoiceStatus'
import ConfirmationModal from '../..//Core/Dialog/ConfirmationModal'

const columnHelper = createColumnHelper()

const Invoices = ({companyId}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const companyInfo = useSelector(state => state.company.companyInfo)

  const permissions = usePermissions([{
    name: 'can_manage_invoices',
    permission: 'invoices_create_edit_drafts',
  }])

  const [invoiceSettings, setInvoiceSettings] = useState({})
  const [{invoices, loading}, setInvoices] = useState({invoices: [], loading: false})
  const [{customers, places, statuses}, setData] = useState({
    customers: [],
    places: [],
    statuses: [{id: 'draft', title: t('Draft'), checked: false, isEmployee: true}, {id: 'unbilled', title: t('Unbilled'), checked: false, isEmployee: true}, {id: 'paid', title: t('Paid'), checked: false, isEmployee: true}]
  })
  const [query, setQuery] = useState('')
  const [filterDate, setFilterDate] = useState({start: moment().startOf('month'), end: moment().endOf('month')})
  const [openActionMenuId, setOpenActionMenuId] = useState(null)
  const [actionMenuPosition, setActionMenuPosition] = useState({top: 0, left: 0})
  const [sorting, setSorting] = useState([])

  const tableBodyRef = useRef(null)
  const previewInvoiceRef = useRef(null)
  const confirmationModalRef = useRef(null)

  const selectedStatuses = useMemo(() => statuses.filter(s => s.checked).map(s => s.id), [statuses])
  const selectedPlaces = useMemo(() => places.filter(p => p.checked).map(p => p.id), [places])
  const selectedCustomers = useMemo(() => customers.filter(c => c.checked).map(c => c.id), [customers])

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(invoice.status)) {
        return false
      }
      if (selectedPlaces.length > 0 && !selectedPlaces.includes(invoice.place_id)) {
        return false
      }
      if (selectedCustomers.length > 0 && !selectedCustomers.includes(invoice.customer_id)) {
        return false
      }
      if (query) {
        const searchLower = query.toLowerCase()
        const invoiceNumber = invoice.invoice_number || ''
        const amount = invoice.total_amount ? String(invoice.total_amount) : ''
        const customerName = invoice.customer?.display_name || invoice.customer?.legal_name || ''

        return (
          invoiceNumber.toLowerCase().includes(searchLower) ||
          amount.toLowerCase().includes(searchLower) ||
          customerName.toLowerCase().includes(searchLower)
        )
      }
      return true
    })
    
  }, [query, invoices, selectedStatuses, selectedPlaces, selectedCustomers])

  const aggregatedData = useMemo(() => {
    const totalInvoices = filteredInvoices.length
    const grossTotal = filteredInvoices.reduce((acc, invoice) => acc + (Number(invoice.total_amount) || 0), 0)
    const vatTax = filteredInvoices.reduce((acc, invoice) => {
      const items = Array.isArray(invoice?.items) ? invoice.items : []
      const invoiceVat = items.filter(item => Number(item.vat)).reduce((itemAcc, item) => itemAcc + ((Number(item.total_price) || 0) - ((Number(item.unit_price) || 0) * (Number(item.quantity) || 0))), 0)
      return acc + Math.max(0, invoiceVat)
    }, 0)
    const netAmount = grossTotal - vatTax

    return {
      totalInvoices,
      netAmount,
      vatTax,
      grossTotal,
    }
  }, [filteredInvoices])

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (e.target instanceof Element && (e.target.closest('[data-action-menu]') || e.target.closest('[data-action-menu-popover]'))) {
        return
      }

      setOpenActionMenuId(null)
    }

    const handleWindowResize = () => {
      setOpenActionMenuId(null)
    }

    document.addEventListener('click', handleDocumentClick)
    window.addEventListener('resize', handleWindowResize)
    window.addEventListener('scroll', handleWindowResize, true)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
      window.removeEventListener('resize', handleWindowResize)
      window.removeEventListener('scroll', handleWindowResize, true)
    }
  }, [])

  useEffect(() => {
    const rect = tableBodyRef.current.getBoundingClientRect()
    const distance = window.innerHeight - rect.top;
    tableBodyRef.current.style.height = `${distance - 40}px`
  }, [])

  useEffect(() => {
    init()
  }, [companyId])

  useEffect(() => {
    fetchInvoices()
  }, [companyId, filterDate.start.format('YYYY-MM-DD'), filterDate.end.format('YYYY-MM-DD')])

  const init = async () => {
    const [placesRes, customersRes, invoiceSettings] = await Promise.all([
      getPlaces(companyId),
      getCustomers(companyId),
      getInvoiceSettings(companyId),
    ])

    setData(prev => ({
      ...prev,
      places: Array.isArray(placesRes) ? placesRes.map(p => ({id: p.id, title: p.name, checked: false, isEmployee: true})) : [],
      customers: Array.isArray(customersRes) ? customersRes.map(c => ({id: c.id, title: c.display_name, checked: false, isEmployee: true})) : [],
    }))
    if (invoiceSettings) {
      setInvoiceSettings(invoiceSettings)
    }
  }

  const fetchInvoices = async () => {
    setInvoices(prev => ({...prev, loading: true}))
    const res = await getInvoices(companyId, {
      from: filterDate.start.format('YYYY-MM-DD'),
      to: filterDate.end.format('YYYY-MM-DD'),
      search: query,
    })
    if (Array.isArray(res)) {
      setInvoices({invoices: res, loading: false})
      return
    }
    setInvoices({invoices: [], loading: false})
  }

  const handleChangeDate = ({startDate, endDate}) => {
    setFilterDate({start: moment(startDate), end: moment(endDate)})
  }

  const handleCreateInvoice = () => {
     history.push({search: `?tab=new_invoice`})
  }

  const handleChangeFilter = (type, data) => {
    setData(prev => ({
      ...prev,
      [type]: data,
    }))
  }

  const handleOpenInvoice = async (invoice) => {
    const invoiceData = {
      ...invoice,
      places: [...(invoice.with_no_place ? [{id: 0, name: t('No place')}] : []), ...invoice.places],
      items: applyMarkUps(
        getGroupedItems(invoice.items, invoice.group_by),
        {
          work_times_markup_type: invoice.work_times_markup_type,
          work_times_markup_value: invoice.work_times_markup_value,
          expenses_markup_type: invoice.expenses_markup_type,
          expenses_markup_value: invoice.expenses_markup_value,
        }
      )
    }
    const ctx = buildInvoiceContext(invoiceData, invoiceSettings, companyInfo)
    const url = await previewInvoicePdfUrl(ctx)
    previewInvoiceRef.current.open({ pdfUrl: url, invoice: invoiceData })
  }

  const handleEditInvoice = (invoiceId) => {
    history.push({search: `?tab=${invoiceId}`})
  }

  const handleRemoveInvoice = async (invoiceId) => {
    const result = await confirmationModalRef.current.show({
      title: t('Are you sure you want to remove this invoice?'),
      description: t('This action cannot be undone.'),
      successButton: t('Remove'),
      rejectButton: t('Cancel'),
    })
    if (result.success) {
      const res = await removeInvoice(companyId, invoiceId)
      if (res) {
        fetchInvoices()
      }
    }
  }

  const handleChangeStatus = async (invoice) => {
    const res = await updateInvoiceStatus(companyId, invoice.id, {status: 'paid'})
    if (res) {
      fetchInvoices()
    }
  }

  const handleDownloadInvoicePdf = async (invoice) => {
     const invoiceData = {
      ...invoice,
      places: [...(invoice.with_no_place ? [{id: 0, name: t('No place')}] : []), ...invoice.places],
      items: applyMarkUps(
        getGroupedItems(invoice.items, invoice.group_by),
        {
          work_times_markup_type: invoice.work_times_markup_type,
          work_times_markup_value: invoice.work_times_markup_value,
          expenses_markup_type: invoice.expenses_markup_type,
          expenses_markup_value: invoice.expenses_markup_value,
        }
      )
    }
    const ctx = buildInvoiceContext(invoiceData, invoiceSettings, companyInfo)
    downloadInvoicePdf(ctx)
  }

  const handleToggleActionMenu = (e, invoiceId) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setActionMenuPosition({
      top: rect.bottom + 6,
      left: rect.right - 144,
    })
    setOpenActionMenuId(prev => (prev === invoiceId ? null : invoiceId))
  }

  const handleMenuAction = (e, action) => {
    e.stopPropagation()
    setOpenActionMenuId(null)
    action()
  }

  const handleExportExcel = () => {
    generateExcelExport(columns, filteredInvoices)
  }

  const activeInvoice = useMemo(() => {
    return invoices.find(invoice => invoice.id === openActionMenuId) || null
  }, [invoices, openActionMenuId])

  const columns = useMemo(() => ([
    columnHelper.accessor(row => row.invoice_number || '', {
      id: 'invoice_number',
      header: t('Invoice number'),
      cell: ({ row, getValue }) => {
        return (
          <div className={styles.invoiceNumberCell}>
            { getValue() || '-' }
            {
              row.original.internal_notes
                ? <div data-tooltip-id='notes' data-tooltip-content={row.original.internal_notes} className={styles.noteIcon}>
                    <NoteIcon />
                  </div>
                : null
            }
          </div>
        )
      },
    }),
    columnHelper.accessor(row => row.customer?.display_name || row.customer?.legal_name || '-', {
      id: 'customer',
      header: t('Customer'),
      cell: ({ row }) => row.original.customer?.display_name || row.original.customer?.legal_name || '-',
    }),
    columnHelper.display({
      id: 'place',
      header: t('Project'),
      cell: ({ row }) => {
        return row.original.places.reduce((acc, p) => [...acc, p.name], row.original.with_no_place ? [t('No place')] : []).join(', ')
      },
    }),
    columnHelper.accessor(row => Number(row.total_amount) || 0, {
      id: 'amount',
      header: t('Amount'),
      cell: ({ getValue }) => numeral(getValue() || 0).format('0.00'),
    }),
    columnHelper.accessor(row => row.currency || '-', {
      id: 'currency',
      header: t('Currency'),
      cell: ({ getValue }) => getValue() || '-',
    }),
    columnHelper.accessor(row => moment(row.invoice_date).valueOf(), {
      id: 'issue_date',
      header: t('Invoice date'),
      cell: ({ row }) => row.original.invoice_date ? moment(row.original.invoice_date).format('YYYY-MM-DD') : '-',
    }),
    columnHelper.accessor(row => moment(row.due_date).valueOf(), {
      id: 'due_date',
      header: t('Due date'),
      cell: ({ row }) => row.original.due_date ? moment(row.original.due_date).format('YYYY-MM-DD') : '-',
    }),
    columnHelper.accessor('marked_as_paid_date', {
      id: 'marked_as_paid_date',
      header: t('Paid in full'),
      cell: ({ getValue }) => getValue() ? moment(getValue()).format('YYYY-MM-DD') : '-',
    }),
    columnHelper.accessor(row => row.status || '-', {
      id: 'status',
      header: t('Status'),
      cell: ({ getValue, row }) => <InvoiceStatus status={getValue()} dueDate={row.original.due_date} />,
    }),
    columnHelper.display({
      id: 'action',
      enableSorting: false,
      header: t('Action'),
      cell: ({ row }) => (
        <div className={styles.actionMenuWrapper} data-action-menu>
          <button
            type='button'
            className={styles.actionButton}
            onClick={e => handleToggleActionMenu(e, row.original.id)}
          >
            <ThreeDotsIcon />
          </button>
        </div>
      ),
    }),
  ]), [columnHelper, t, openActionMenuId])

  const table = useReactTable({
    data: Array.isArray(filteredInvoices) ? filteredInvoices : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{flex: 1}}>
          <div className={styles.headerTitle}>{t('Invoices')}</div>
          <div className={styles.headerDesc}>{t('All issued and draft invoices. Generate from clocked hours and project expenses.')}</div>
        </div>
        <DRP
          right={true}
          style={{top: 0}}
          definedRangesStyle={{top: 65, right: 190}}
          initRange={{startDate: filterDate.start.toDate(), endDate: filterDate.end.toDate()}}
          onChange={handleChangeDate} />
        {
          permissions.can_manage_invoices
            ? <button type="button" className={styles.button} onClick={handleCreateInvoice}>
                <PlusIcon />
                { t('New Invoice') }
              </button>
            : null
        }
      </div>
      <div className={styles.aggregatedDataContainer}>
        <div className={styles.card}>
          <div className={styles.label}>{t('Total invoices')}</div>
          <div className={styles.value}>{aggregatedData.totalInvoices}</div>
        </div>
        <div className={styles.card}>
          <div className={styles.label}>{t('Net amount')}</div>
          <div className={styles.value}>
            {numeral(aggregatedData.netAmount).format('0.00')}
            <span style={{fontSize: 14}}> {getCurrency(invoiceSettings.default_currency).symbol}</span>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.label}>{t('VAT / tax')}</div>
          <div className={styles.value}>
            {numeral(aggregatedData.vatTax).format('0.00')}
            <span style={{fontSize: 14}}> {getCurrency(invoiceSettings.default_currency).symbol}</span>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.label}>{t('Gross total')}</div>
          <div className={styles.value}>
            <span style={{color: '#0085FF'}}>{numeral(aggregatedData.grossTotal).format('0.00')}</span>
            <span style={{fontSize: 14}}> {getCurrency(invoiceSettings.default_currency).symbol}</span>
          </div>
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.tools}>
        <input
          type="text"
          placeholder={t('Search invoice #, amount or customer')}
          className={styles.searchInput}
          value={query}
          onChange={(e) => setQuery(e.target.value)} />
        <CustomSelect
          placeholder={t('All statuses')}
          items={statuses}
          onChange={data => handleChangeFilter('statuses', data)}
        />
        <CustomSelect
          placeholder={t('All places')}
          items={places}
          onChange={data => handleChangeFilter('places', data)}
        />
        <CustomSelect
          placeholder={t('All customers')}
          buttonLabel={t('Filter')}
          items={customers}
          onChange={data => handleChangeFilter('customers', data)}
          className={styles.select} />
        <button
          type="button"
          className={styles.excelButton}
          data-tooltip-id="excel"
          data-tooltip-content={t('Export to Excel')}
          onClick={handleExportExcel}>
          <ExcelIcon />
        </button>
      </div>
      <Table
        ref={tableBodyRef}
        table={table}
        columns={columns}
        loading={loading}
        wrapperStyle={{overflow: 'auto'}}
        headerClassName={styles.tableHeader} />
      {openActionMenuId && activeInvoice && (
        <div
          className={styles.actionMenu}
          data-action-menu-popover
          style={{ top: actionMenuPosition.top, left: actionMenuPosition.left }}
        >
          {
            activeInvoice.status === 'sent'
              ? <button
                  type='button'
                  className={styles.actionMenuItem}
                  onClick={e => handleMenuAction(e, () => handleChangeStatus(activeInvoice))}
                >
                  {t(`Mark as paid`)}
                </button>
              : null
          }
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handleOpenInvoice(activeInvoice))}
          >
            {t('View')}
          </button>
          {
            permissions.can_manage_invoices && activeInvoice.status === 'draft'
              ? <button
                  type='button'
                  className={styles.actionMenuItem}
                  onClick={e => handleMenuAction(e, () => handleEditInvoice(activeInvoice.id))}
                >
                  {t('Edit')}
                </button>
              : null
          }
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handleDownloadInvoicePdf(activeInvoice))}
          >
            {t('Download PDF')}
          </button>
          {
            permissions.can_manage_invoices
              ? <button
                  type='button'
                  className={styles.actionMenuItem}
                  onClick={e => handleMenuAction(e, () => handleRemoveInvoice(activeInvoice.id))}
                >
                  {t('Remove')}
                </button>
              : null
          }
        </div>
      )}
      <PreviewInvoice ref={previewInvoiceRef} />
      <ConfirmationModal ref={confirmationModalRef} />
      <Tooltip
        id='notes'
        effect='solid'
        className={styles.tooltip} />
      <Tooltip
        id='excel'
        effect='solid'
        place="top-start"
        className={cn(styles.tooltip, styles.excel)}
      />
    </div>
  )
}

export default Invoices
